import { Box, Button, ButtonGroup, Card, Chip, Grid, Tab, Tabs, Typography } from "@mui/material"
import { MainLoggedInLayout } from "../../components/layouts/main.loggedIn.layout"
import { useNavigate } from "react-router"
import { useContext, useEffect, useMemo, useState } from "react"
import { useQuery } from "@tanstack/react-query"
import { UserContext } from "../../context/UserContext"
import { searchAllEvents } from "../../controllers/event/event"
import { CategoryDisplay } from "../../components/category.display"
import { SearchWidget } from "../../components/search.widget"
import CategoryFilter from "../../components/category.filter"
import LocationFilter from "../../components/location.filter"
import { ShareLocation, Tune } from "@mui/icons-material"
import { useProfileComplete } from "../../hooks/useProfileComplete"

export const CommunityCard = ({ card_details, navigate }) => (
    <Card
        onClick={() => navigate("/event/show/" + card_details?._id)}
        sx={{
            position: "relative",
            cursor: "pointer",
            height: 250,
            borderRadius: 2,
            overflow: "hidden",
            transition: "transform 0.2s, box-shadow 0.2s",
            "&:hover": { transform: "translateY(-4px)", boxShadow: "0 8px 25px rgba(0,0,0,0.15)" },
        }}
    >
        <Box
            sx={{
                backgroundSize: "cover",
                backgroundPosition: "center",
                backgroundImage: `url('${card_details.header_img}')`,
                height: "100%",
                position: "relative",
            }}
        >
            <Box
                sx={{
                    position: "absolute",
                    inset: 0,
                    background: "linear-gradient(to bottom, rgba(0,0,0,0.25) 0%, rgba(0,0,0,0.72) 100%)",
                }}
            />
            <Box sx={{ position: "absolute", bottom: 0, left: 0, right: 0, p: 2, color: "#fff" }}>
                <Typography
                    variant="h6"
                    sx={{
                        color: "#fff",
                        fontWeight: 800,
                        mb: 0.75,
                        fontSize: "1rem",
                        lineHeight: 1.3,
                        display: "-webkit-box",
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: "vertical",
                        overflow: "hidden",
                    }}
                >
                    {card_details.title}
                </Typography>
                <Typography variant="body2" sx={{ mb: 0.5, opacity: 0.9, fontSize: 12 }}>
                    {new Date(card_details.start_date_time).toDateString()}
                </Typography>
                <Typography
                    variant="body2"
                    sx={{ mb: 0.5, opacity: 0.85, fontSize: 11, display: "-webkit-box", WebkitLineClamp: 1, WebkitBoxOrient: "vertical", overflow: "hidden" }}
                >
                    {card_details.is_online ? "💻 Online event" : card_details.location?.formatted_address}
                </Typography>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 0.5 }}>
                    <Chip
                        size="small"
                        label={`${card_details.RSVP?.length ?? 0} attending`}
                        sx={{ background: "rgba(255,255,255,0.2)", color: "#fff", fontSize: 10, height: 20 }}
                    />
                    <CategoryDisplay label={card_details.category} hideLabel={true} />
                </Box>
            </Box>
        </Box>
    </Card>
)

function TabPanel({ children, value, index }) {
    return value === index ? <Box>{children}</Box> : null
}

export const ListEvent = () => {
    const { userDetails } = useContext(UserContext)
    const token = userDetails?.token
    useProfileComplete(userDetails)

    const [showCatFilter, setShowCatFilter] = useState(false)
    const [showLocFilter, setShowLocFilter] = useState(false)
    const [title, setTitle] = useState("")
    const [page_number, setPageNum] = useState(0)
    const [miles, setMiles] = useState(10)
    const [lat, setLat] = useState("")
    const [lng, setLng] = useState("")
    const [category, setCategory] = useState("")
    const [categories, setCategories] = useState([])
    const limit = 8

    const { data: events, refetch } = useQuery({
        queryKey: ["searchAllEvents"],
        queryFn: () => searchAllEvents({ token, title, page_number, category, limit, lat, lng, miles }),
    })
    useEffect(() => { refetch() })

    const listAllEvents = useMemo(() => events?.event ?? [], [events?.event])
    const [allEvents, setAllEvents] = useState([])
    const [tempEvents, setTempEvents] = useState([])

    useEffect(() => {
        listAllEvents.forEach(item => setTempEvents(prev => [...prev, item]))
        const localCats = []
        listAllEvents.forEach(item => {
            if (item.category && !localCats.includes(item.category)) localCats.push(item.category)
        })
        setCategories(localCats)
    }, [listAllEvents])

    useEffect(() => {
        const unique = Array.from(new Map(tempEvents.map(e => [JSON.stringify(e), e])).values())
        setAllEvents(unique)
    }, [tempEvents])

    useEffect(() => {
        setPageNum(0)
        if (title || category || lat) {
            setAllEvents([])
            setTempEvents([])
        }
    }, [title, category, lat])

    const handleScroll = (e) => {
        const atBottom = e.target.scrollHeight - e.target.scrollTop - e.target.clientHeight < 10
        if (atBottom && listAllEvents.length === limit) {
            setTimeout(() => setPageNum(p => p + 1), 100)
        }
    }

    const navigate = useNavigate()
    const [tab, setTab] = useState(1)

    const myEvents = allEvents.filter(e =>
        e.author?.includes(userDetails?.id) &&
        (!category || e.category === category) &&
        (!title || e.title?.toLowerCase().includes(title.toLowerCase()))
    )

    const allFiltered = allEvents.filter(e =>
        (!category || e.category === category) &&
        (!title || e.title?.toLowerCase().includes(title.toLowerCase()))
    )

    return (
        <MainLoggedInLayout>
            {/* Header */}
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2, flexWrap: "wrap", gap: 1 }}>
                <Typography variant="h5" fontWeight={800} sx={{
                    background: "linear-gradient(90deg,#E40303,#732982)",
                    WebkitBackgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                }}>
                    {tab === 0 ? "My Events" : "All Events"}
                </Typography>
                {token && (
                    <Button
                        variant="contained"
                        onClick={() => navigate("/event/create/" + null)}
                        sx={{ borderRadius: 2, textTransform: "none", fontWeight: 700, background: "#732982", "&:hover": { background: "#5c1f68" } }}
                    >
                        Create Event
                    </Button>
                )}
            </Box>

            {/* Tabs */}
            <Tabs
                value={tab}
                onChange={(_, v) => setTab(v)}
                sx={{
                    mb: 2,
                    "& .MuiTab-root": { textTransform: "none", fontWeight: 700 },
                    "& .MuiTabs-indicator": { background: "linear-gradient(90deg,#E40303,#732982)" },
                }}
            >
                <Tab label="My Events" />
                <Tab label="All Events" />
            </Tabs>

            {/* Filters */}
            <Box sx={{ mb: 2 }}>
                <SearchWidget search={title} setSearch={setTitle} />
                <Box sx={{ display: "flex", gap: 1, mt: 1.5, flexWrap: "wrap" }}>
                    <Button
                        size="small"
                        variant={showCatFilter ? "contained" : "outlined"}
                        onClick={() => setShowCatFilter(v => !v)}
                        sx={{ borderRadius: 50, textTransform: "none" }}
                        endIcon={<Tune fontSize="small" />}
                    >
                        Category
                    </Button>
                    <Button
                        size="small"
                        variant={showLocFilter ? "contained" : "outlined"}
                        onClick={() => setShowLocFilter(v => !v)}
                        sx={{ borderRadius: 50, textTransform: "none" }}
                        endIcon={<ShareLocation fontSize="small" />}
                    >
                        Location
                    </Button>
                    {(title || category || lat) && (
                        <Button
                            size="small"
                            variant="outlined"
                            color="error"
                            onClick={() => {
                                setTitle(""); setCategory(""); setLat(""); setLng(""); setMiles(10)
                                setShowCatFilter(false); setShowLocFilter(false)
                            }}
                            sx={{ borderRadius: 50, textTransform: "none" }}
                        >
                            Clear Filters
                        </Button>
                    )}
                </Box>
                {showCatFilter && <Box sx={{ mt: 1.5 }}><CategoryFilter catgories={categories} category={category} setCategory={setCategory} /></Box>}
                {showLocFilter && <Box sx={{ mt: 1.5 }}><LocationFilter miles={miles} setMiles={setMiles} setLat={setLat} setLng={setLng} /></Box>}
            </Box>

            {/* Content — scrollable */}
            <Box onScroll={handleScroll} sx={{ overflowY: "auto", maxHeight: "65vh", pr: 0.5 }}>
                <TabPanel value={tab} index={0}>
                    {myEvents.length > 0 ? (
                        <Grid container spacing={2}>
                            {myEvents.map((event, i) => (
                                <Grid item xs={12} sm={6} md={4} key={i}>
                                    <CommunityCard card_details={event} navigate={navigate} />
                                </Grid>
                            ))}
                        </Grid>
                    ) : (
                        <Box sx={{ textAlign: "center", py: 8 }}>
                            <Typography variant="h6" color="text.secondary" gutterBottom>
                                You haven't created any events yet
                            </Typography>
                            <Box sx={{ display: "flex", gap: 1, justifyContent: "center", mt: 2 }}>
                                <Button variant="contained" onClick={() => setTab(1)}>Browse Events</Button>
                                {token && (
                                    <Button variant="outlined" onClick={() => navigate("/event/create/" + null)}>Create One</Button>
                                )}
                            </Box>
                        </Box>
                    )}
                </TabPanel>

                <TabPanel value={tab} index={1}>
                    {allFiltered.length > 0 ? (
                        <Grid container spacing={2}>
                            {allFiltered.map((event, i) => (
                                <Grid item xs={12} sm={6} md={4} key={i}>
                                    <CommunityCard card_details={event} navigate={navigate} />
                                </Grid>
                            ))}
                        </Grid>
                    ) : (
                        <Box sx={{ textAlign: "center", py: 8 }}>
                            <Typography variant="h6" color="text.secondary" gutterBottom>No events found</Typography>
                            {token && (
                                <Button variant="contained" onClick={() => navigate("/event/create/" + null)} sx={{ mt: 2 }}>
                                    Create the First Event
                                </Button>
                            )}
                        </Box>
                    )}
                </TabPanel>
            </Box>
        </MainLoggedInLayout>
    )
}
